import { exportToCSV, exportToExcel, importFromFile, triggerFileInput } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { formatTreasuryExportRows } from './treasurySaudiHelpers';

export function operationExportHeaders(p) {
  return [
    { key: 'date', label: p('th_date') },
    { key: 'designation', label: p('th_designation') },
    { key: 'credit', label: p('th_credit') },
    { key: 'debit', label: p('th_debit') },
    { key: 'solde', label: p('th_balance') },
    { key: 'affectation', label: p('th_affectation') },
    { key: 'caisse', label: p('th_caisse') },
  ];
}

export function mapOperationsForExport(operations) {
  return (operations || []).map((op) => ({
    date: op.date || op.operation_date || '',
    designation: op.designation || op.description || '',
    credit: op.credit || 0,
    debit: op.debit || 0,
    solde: op.solde ?? '',
    affectation: op.affectation || '',
    caisse: op.caisse || op.caisse_name || '',
  }));
}

export function voucherExportHeaders(p) {
  return [
    { key: 'invoiceNumber', label: p('col_number') },
    { key: 'date', label: p('col_date') },
    { key: 'clientName', label: p('col_beneficiary') },
    { key: 'total', label: p('col_amount') },
    { key: 'type', label: p('col_type') },
    { key: 'status', label: p('col_status') },
    { key: 'designation', label: p('col_designation') },
  ];
}

export function mapVouchersForExport(bons) {
  return (bons || []).map((bon) => ({
    invoiceNumber: bon.invoiceNumber || '',
    date: bon.date || '',
    clientName: bon.clientName || '',
    total: bon.total || 0,
    type: bon.type || '',
    status: bon.status || '',
    designation: bon.designation || '',
  }));
}

export function transferExportHeaders(p) {
  return [
    { key: 'reference', label: p('col_reference') },
    { key: 'date', label: p('col_date') },
    { key: 'source', label: p('col_source') },
    { key: 'destination', label: p('col_destination') },
    { key: 'montant', label: p('col_amount') },
    { key: 'motif', label: p('col_motif') },
    { key: 'status', label: p('col_status') },
  ];
}

export function mapTransfersForExport(transfers) {
  return (transfers || []).map((t) => ({
    reference: t.reference || '',
    date: t.date || '',
    source: t.source || '',
    destination: t.destination || '',
    montant: t.montant || 0,
    motif: t.motif || '',
    status: t.status || '',
  }));
}

export function closureExportHeaders(p) {
  return [
    { key: 'date', label: p('th_date') },
    { key: 'caissier', label: p('th_cashier') },
    { key: 'soldeOuverture', label: p('th_opening') },
    { key: 'totalEntrees', label: p('th_receipts') },
    { key: 'totalSorties', label: p('th_payments') },
    { key: 'soldeFermeture', label: p('th_expected') },
    { key: 'soldeReel', label: p('th_actual') },
    { key: 'ecart', label: p('th_gap') },
    { key: 'status', label: p('th_status') },
  ];
}

export function mapClosuresForExport(closures) {
  return (closures || []).map((c) => ({
    date: c.date || '',
    caissier: c.caissier || c.caisse_name || '',
    soldeOuverture: c.soldeOuverture ?? 0,
    totalEntrees: c.totalEntrees ?? 0,
    totalSorties: c.totalSorties ?? 0,
    soldeFermeture: c.soldeFermeture ?? 0,
    soldeReel: c.soldeReel ?? 0,
    ecart: c.ecart ?? 0,
    status: c.status || '',
  }));
}

export function reconciliationExportHeaders(p) {
  return [
    { key: 'date', label: p('col_date') },
    { key: 'periode', label: p('col_period') },
    { key: 'caisse', label: p('col_register') },
    { key: 'soldeSysteme', label: p('col_system_balance') },
    { key: 'soldePhysique', label: p('col_physical_balance') },
    { key: 'ecart', label: p('col_gap') },
    { key: 'status', label: p('col_status') },
  ];
}

export function mapReconciliationsForExport(rows) {
  return (rows || []).map((r) => ({
    date: r.date || '',
    periode: r.periode || '',
    caisse: r.caisse || '',
    soldeSysteme: r.soldeSysteme ?? 0,
    soldePhysique: r.soldePhysique ?? 0,
    ecart: r.ecart ?? 0,
    status: r.status || '',
  }));
}

function buildPdfTableHtml(headers, rows) {
  return `
    <div class="report-content">
      <table>
        <thead>
          <tr>${headers.map((h) => `<th>${h.label}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) =>
                `<tr>${headers
                  .map((h) => `<td>${row[h.key] ?? ''}</td>`)
                  .join('')}</tr>`
            )
            .join('')}
        </tbody>
      </table>
    </div>
  `;
}

export async function exportTreasuryCSV(rows, headers, filename) {
  if (!rows?.length) return false;
  exportToCSV(formatTreasuryExportRows(rows), headers, filename);
  return true;
}

export async function exportTreasuryExcel(rows, headers, filename, sheetName) {
  if (!rows?.length) return false;
  await exportToExcel(formatTreasuryExportRows(rows), headers, filename, sheetName);
  return true;
}

export async function exportTreasuryPDF(title, headers, rows) {
  if (!rows?.length) return false;
  const formattedRows = formatTreasuryExportRows(rows);
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = buildPdfTableHtml(headers, formattedRows);
  document.body.appendChild(tempDiv);
  try {
    await generateReportPDF('treasury', title, {});
    return true;
  } finally {
    document.body.removeChild(tempDiv);
  }
}

export function operationImportHeaders(p) {
  return [
    { key: 'date', label: p('th_date') },
    { key: 'designation', label: p('th_designation') },
    { key: 'credit', label: p('th_credit') },
    { key: 'debit', label: p('th_debit') },
    { key: 'affectation', label: p('th_affectation') },
  ];
}

export function voucherImportHeaders(p) {
  return [
    { key: 'date', label: p('col_date') },
    { key: 'clientName', label: p('col_beneficiary') },
    { key: 'total', label: p('col_amount') },
    { key: 'designation', label: p('col_designation') },
    { key: 'type', label: p('col_type') },
  ];
}

export function transferImportHeaders(p) {
  return [
    { key: 'date', label: p('col_date') },
    { key: 'source', label: p('col_source') },
    { key: 'destination', label: p('col_destination') },
    { key: 'montant', label: p('col_amount') },
    { key: 'motif', label: p('col_motif') },
  ];
}

export function closureImportHeaders(p) {
  return [
    { key: 'date', label: p('th_date') },
    { key: 'soldeOuverture', label: p('th_opening') },
    { key: 'totalEntrees', label: p('th_receipts') },
    { key: 'totalSorties', label: p('th_payments') },
    { key: 'soldeReel', label: p('th_actual') },
  ];
}

export function reconciliationImportHeaders(p) {
  return [
    { key: 'date', label: p('col_date') },
    { key: 'periode', label: p('col_period') },
    { key: 'soldeSysteme', label: p('col_system_balance') },
    { key: 'soldePhysique', label: p('col_physical_balance') },
  ];
}

export function triggerTreasuryImport(onFileSelect, accept = '.csv,.xlsx,.xls') {
  triggerFileInput(onFileSelect, accept);
}

export async function parseTreasuryImport(file, headers, schema) {
  return importFromFile(file, headers, schema);
}

/** Build CSV string for programmatic verification (tests). */
export function buildCsvString(rows, headers) {
  if (!rows?.length) return '';
  const headerRow = headers.map((h) => `"${h.label}"`).join(',');
  const dataRows = rows.map((item) =>
    headers
      .map((header) => {
        let value = item[header.key];
        if (value == null) value = '';
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(',')
  );
  return [headerRow, ...dataRows].join('\n');
}
